"use client";

import type {
  AcademyCategory,
  SearchSuggestions,
} from "@nyntax/contracts/schemas/course";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@nyntax/design-system/components/ui/avatar";
import { cn } from "@nyntax/design-system/lib/utils";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent, ReactNode } from "react";
import {
  LuArrowUpLeft,
  LuHistory,
  LuLoaderCircle,
  LuSearch,
  LuSearchX,
  LuX,
} from "react-icons/lu";

import { CATEGORY_ICONS } from "@/components/catalog/category-nav";
import { CourseCover } from "@/components/course-cover";
import { catalogPath } from "@/lib/catalog-query";
import { instructorPath } from "@/lib/instructor";
import {
  forgetRecentSearch,
  readRecentSearches,
  saveRecentSearch,
} from "@/lib/recent-searches";
import {
  NO_SUGGESTIONS,
  fetchSuggestions,
  highlightParts,
  matchTopics,
} from "@/lib/search-suggestions";

/** Translated words of the header search and its tray. */
export interface HeaderSearchLabels {
  clear: string;
  clearRecent: string;
  /** "{count} courses". */
  courseCount: string;
  courseCountOne: string;
  courses: string;
  /** "by {name}". */
  by: string;
  instructors: string;
  label: string;
  /** "No matches for “{q}”". */
  noResults: string;
  placeholder: string;
  popularTopics: string;
  recent: string;
  /** "Remove {q}". */
  removeRecent: string;
  /** "See all results for “{q}”". */
  seeAll: string;
  topic: string;
  topics: string;
}

interface HeaderSearchProps {
  categories: { label: string; value: AcademyCategory }[];
  labels: HeaderSearchLabels;
  locale: string;
}

/** Wait this long after the last key before asking for suggestions. */
const DEBOUNCE_MS = 150;

interface Option {
  href: string;
  id: string;
  /** The text to remember as a recent search when this option is chosen. */
  remember: string;
}

const fill = (template: string, values: Record<string, string>) =>
  template.replaceAll(/\{(\w+)\}/gu, (match, key: string) => values[key] ?? match);

const Highlight = ({ query, text }: { query: string; text: string }) => (
  <>
    {highlightParts(text, query).map((part, index) =>
      part.match ? (
        <mark className="text-primary bg-transparent font-semibold" key={index}>
          {part.text}
        </mark>
      ) : (
        <span key={index}>{part.text}</span>
      )
    )}
  </>
);

const GroupTitle = ({ children, id }: { children: ReactNode; id: string }) => (
  <p
    className="text-muted-foreground px-3 pt-3 pb-1.5 text-[11px] font-semibold tracking-wider uppercase"
    id={id}
  >
    {children}
  </p>
);

/**
 * The header search with a suggestion tray. Empty, it offers recent searches
 * and popular topics; as the visitor types it suggests matching topics, up to
 * five courses and the instructors whose name matches, with the typed part in
 * bold. Arrow keys move through the tray, Enter opens the highlighted entry
 * (or searches the Catalog), Escape closes it. Without scripts it is still a
 * plain GET form to the Catalog.
 *
 * @param props - The categories, the translated words and the locale.
 * @returns The search box and its tray.
 */
export const HeaderSearch = ({ categories, labels, locale }: HeaderSearchProps) => {
  const router = useRouter();
  const baseId = useId();
  const listId = `${baseId}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [recent, setRecent] = useState<string[]>([]);
  const [result, setResult] = useState<{ q: string; data: SearchSuggestions }>({
    q: "",
    data: NO_SUGGESTIONS,
  });

  const query = text.trim();
  const loading = query !== "" && result.q !== query;

  useEffect(() => {
    setRecent(readRecentSearches());
  }, []);

  useEffect(() => {
    if (query === "") {
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void fetchSuggestions(query, controller.signal).then((data) => {
        if (!controller.signal.aborted) {
          setResult({ q: query, data });
        }
      });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Until the newest answer arrives, keep showing the last one.
  const { courses, instructors } = result.data;
  const topics = matchTopics(query, categories);
  const searchHref = catalogPath(locale, { q: query });

  const optionId = (key: string) => `${baseId}-${key}`;
  const options: Option[] = [];

  if (query === "") {
    for (const [index, entry] of recent.entries()) {
      options.push({ id: optionId(`r-${index}`), href: catalogPath(locale, { q: entry }), remember: entry });
    }

    for (const topic of categories) {
      options.push({ id: optionId(`p-${topic.value}`), href: catalogPath(locale, { category: topic.value }), remember: "" });
    }
  } else {
    for (const topic of topics) {
      options.push({ id: optionId(`t-${topic.value}`), href: catalogPath(locale, { category: topic.value }), remember: query });
    }

    for (const course of courses) {
      options.push({ id: optionId(`c-${course.slug}`), href: `/${locale}/courses/${encodeURIComponent(course.slug)}`, remember: query });
    }

    for (const person of instructors) {
      options.push({ id: optionId(`i-${person.id}`), href: instructorPath(locale, person.id), remember: query });
    }
  }

  const hasMatches = topics.length + courses.length + instructors.length > 0;
  const showNoResults = query !== "" && !loading && !hasMatches;

  if (query !== "" && hasMatches) {
    options.push({ id: optionId("all"), href: searchHref, remember: query });
  }

  const activeId = active >= 0 ? options[active]?.id : undefined;

  useEffect(() => {
    if (activeId !== undefined) {
      document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
    }
  }, [activeId]);

  const indexOf = (id: string) => options.findIndex((option) => option.id === id);
  const activeOption = active >= 0 ? options[active] : undefined;
  const trayVisible = open && (options.length > 0 || showNoResults);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  const choose = (option: Option) => {
    if (option.remember !== "") {
      setRecent(saveRecentSearch(option.remember));
    }

    close();
    inputRef.current?.blur();
  };

  const go = (option: Option) => {
    choose(option);
    router.push(option.href);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (activeOption !== undefined) {
      go(activeOption);

      return;
    }

    if (query !== "") {
      go({ id: "submit", href: searchHref, remember: query });
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);

      if (options.length === 0) {
        return;
      }

      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => {
        const from = current < 0 && step < 0 ? options.length : current;

        return (from + step + options.length) % options.length;
      });

      return;
    }

    if (event.key === "Escape") {
      if (open) {
        close();
      } else {
        setText("");
      }
    }
  };

  /** Props every option in the tray shares: id, highlight, and keeping focus in the box. */
  const optionProps = (option: Option | undefined) => {
    if (option === undefined) {
      return {};
    }

    const index = indexOf(option.id);

    return {
      "aria-selected": active === index,
      id: option.id,
      role: "option" as const,
      tabIndex: -1,
      onClick: () => {
        choose(option);
      },
      onMouseDown: (event: { preventDefault: () => void }) => {
        event.preventDefault();
      },
      onMouseMove: () => {
        if (active !== index) {
          setActive(index);
        }
      },
    };
  };

  const rowClass = (option: Option | undefined) =>
    cn(
      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm outline-none",
      option !== undefined && active === indexOf(option.id) ? "bg-primary/10" : ""
    );

  const optionFor = (prefix: string, key: string) =>
    options.find((option) => option.id === optionId(`${prefix}-${key}`));

  const allOption = options.find((option) => option.id === optionId("all"));

  return (
    <form
      action={`/${locale}`}
      className="relative"
      method="get"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          close();
        }
      }}
      onSubmit={submit}
      role="search"
    >
      <label className="sr-only" htmlFor={`${baseId}-input`}>
        {labels.label}
      </label>
      <div className="relative">
        <LuSearch
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2"
        />
        <input
          aria-activedescendant={activeOption?.id}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={trayVisible}
          autoComplete="off"
          className={cn(
            "bg-muted/60 placeholder:text-muted-foreground focus-visible:bg-background focus-visible:ring-ring h-10 w-full rounded-full border border-transparent pr-10 pl-10 text-sm transition outline-none focus-visible:ring-2 [&::-webkit-search-cancel-button]:hidden",
            trayVisible ? "bg-background ring-ring ring-2" : ""
          )}
          enterKeyHint="search"
          id={`${baseId}-input`}
          maxLength={100}
          name="q"
          onChange={(event) => {
            setText(event.target.value);
            setActive(-1);
            setOpen(true);
          }}
          onFocus={() => {
            setRecent(readRecentSearches());
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder={labels.placeholder}
          ref={inputRef}
          role="combobox"
          type="search"
          value={text}
        />
        {loading ? (
          <LuLoaderCircle
            aria-hidden="true"
            className="text-muted-foreground absolute top-1/2 right-3.5 size-4 -translate-y-1/2 animate-spin"
          />
        ) : null}
        {!loading && text !== "" ? (
          <button
            aria-label={labels.clear}
            className="text-muted-foreground hover:text-foreground hover:bg-muted absolute top-1/2 right-2 grid size-7 -translate-y-1/2 place-items-center rounded-full"
            onClick={() => {
              setText("");
              setActive(-1);
              inputRef.current?.focus();
            }}
            type="button"
          >
            <LuX aria-hidden="true" className="size-4" />
          </button>
        ) : null}
      </div>

      <div
        className={cn(
          "bg-popover text-popover-foreground absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border shadow-2xl shadow-black/10",
          trayVisible ? "" : "hidden"
        )}
      >
        <div className="max-h-[min(36rem,75vh)] overflow-y-auto p-1.5" id={listId} role="listbox">
          {query === "" && recent.length > 0 ? (
            <div aria-labelledby={`${baseId}-recent`} role="group">
              <div className="flex items-center justify-between pr-2">
                <GroupTitle id={`${baseId}-recent`}>{labels.recent}</GroupTitle>
                <button
                  className="text-muted-foreground hover:text-foreground pt-1.5 text-xs font-medium"
                  onClick={() => {
                    setRecent(forgetRecentSearch());
                    inputRef.current?.focus();
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  tabIndex={-1}
                  type="button"
                >
                  {labels.clearRecent}
                </button>
              </div>
              {recent.map((entry, index) => {
                const option = optionFor("r", String(index));

                return (
                  <div className="group/recent relative" key={entry}>
                    <Link className={rowClass(option)} href={option?.href ?? searchHref} {...optionProps(option)}>
                      <LuHistory aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{entry}</span>
                    </Link>
                    <button
                      aria-label={fill(labels.removeRecent, { q: entry })}
                      className="text-muted-foreground hover:text-foreground hover:bg-background absolute top-1/2 right-2 grid size-7 -translate-y-1/2 place-items-center rounded-full opacity-60 group-hover/recent:opacity-100"
                      onClick={() => {
                        setRecent(forgetRecentSearch(entry));
                        setActive(-1);
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      tabIndex={-1}
                      type="button"
                    >
                      <LuX aria-hidden="true" className="size-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}

          {query === "" ? (
            <div aria-labelledby={`${baseId}-popular`} role="group">
              <GroupTitle id={`${baseId}-popular`}>{labels.popularTopics}</GroupTitle>
              <div className="flex flex-wrap gap-2 px-3 pt-1 pb-3">
                {categories.map((topic) => {
                  const option = optionFor("p", topic.value);
                  const Icon = CATEGORY_ICONS[topic.value];
                  const selected = option !== undefined && active === indexOf(option.id);

                  return (
                    <Link
                      className={cn(
                        "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
                        selected ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"
                      )}
                      href={option?.href ?? searchHref}
                      key={topic.value}
                      {...optionProps(option)}
                    >
                      <Icon aria-hidden="true" className="size-3.5" />
                      {topic.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ) : null}

          {topics.length === 0 ? null : (
            <div aria-labelledby={`${baseId}-topics`} role="group">
              <GroupTitle id={`${baseId}-topics`}>{labels.topics}</GroupTitle>
              {topics.map((topic) => {
                const option = optionFor("t", topic.value);
                const Icon = CATEGORY_ICONS[topic.value];

                return (
                  <Link className={rowClass(option)} href={option?.href ?? searchHref} key={topic.value} {...optionProps(option)}>
                    <span className="bg-primary/10 text-primary grid size-9 shrink-0 place-items-center rounded-lg">
                      <Icon aria-hidden="true" className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        <Highlight query={query} text={topic.label} />
                      </span>
                      <span className="text-muted-foreground block text-xs">{labels.topic}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {courses.length === 0 || query === "" ? null : (
            <div aria-labelledby={`${baseId}-courses`} role="group">
              <GroupTitle id={`${baseId}-courses`}>{labels.courses}</GroupTitle>
              {courses.map((course) => {
                const option = optionFor("c", course.slug);

                return (
                  <Link className={rowClass(option)} href={option?.href ?? searchHref} key={course.slug} {...optionProps(option)}>
                    <span className="w-16 shrink-0 overflow-hidden rounded-md border">
                      <CourseCover
                        fallback={{ category: course.category, title: course.title }}
                        fallbackTitle={false}
                        src={course.coverUrl}
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        <Highlight query={query} text={course.title} />
                      </span>
                      {course.instructorName.trim() === "" ? null : (
                        <span className="text-muted-foreground block truncate text-xs">
                          {fill(labels.by, { name: course.instructorName.trim() })}
                        </span>
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {instructors.length === 0 || query === "" ? null : (
            <div aria-labelledby={`${baseId}-instructors`} role="group">
              <GroupTitle id={`${baseId}-instructors`}>{labels.instructors}</GroupTitle>
              {instructors.map((person) => {
                const option = optionFor("i", person.id);

                return (
                  <Link className={rowClass(option)} href={option?.href ?? searchHref} key={person.id} {...optionProps(option)}>
                    <Avatar className="size-9 shrink-0" size="default">
                      {person.photoUrl === null ? null : <AvatarImage alt="" src={person.photoUrl} />}
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        {person.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        <Highlight query={query} text={person.name} />
                      </span>
                      <span className="text-muted-foreground block truncate text-xs">
                        {[
                          fill(person.courseCount === 1 ? labels.courseCountOne : labels.courseCount, {
                            count: new Intl.NumberFormat(locale).format(person.courseCount),
                          }),
                          person.headline,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {showNoResults ? (
            <p className="text-muted-foreground flex items-center gap-3 px-3 py-4 text-sm" role="status">
              <LuSearchX aria-hidden="true" className="size-4 shrink-0" />
              {fill(labels.noResults, { q: query })}
            </p>
          ) : null}

          {allOption === undefined ? null : (
            <Link
              className={cn(rowClass(allOption), "text-primary mt-1 border-t font-medium")}
              href={allOption.href}
              {...optionProps(allOption)}
            >
              <LuSearch aria-hidden="true" className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{fill(labels.seeAll, { q: query })}</span>
              <LuArrowUpLeft aria-hidden="true" className="size-4 shrink-0 rotate-90" />
            </Link>
          )}
        </div>
      </div>
    </form>
  );
};
